import { useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { taskService } from '@/services/task.service';

interface UseTaskTimerProps {
  taskId: string;
  initialTimerStartedAt?: string | null;
  initialAccumulatedSeconds?: number;
  initialPhaseStatus?: string;
  enabled?: boolean;
}

interface TimerState {
  isRunning: boolean;
  isPaused: boolean;
  hasStarted: boolean;
  displaySeconds: number;
}

function formatTime(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
}

export function useTaskTimer({
  taskId,
  initialTimerStartedAt,
  initialAccumulatedSeconds = 0,
  initialPhaseStatus = 'not_started',
  enabled = true,
}: UseTaskTimerProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [isLoading, setIsLoading] = useState(true);
  const [isStarting, setIsStarting] = useState(false);
  const [isPausing, setIsPausing] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);

  const [timerState, setTimerState] = useState<TimerState>({
    isRunning: false,
    isPaused: false,
    hasStarted: false,
    displaySeconds: 0,
  });

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timerStartedAtRef = useRef<string | null>(null);
  const prevAccumulatedSecondsRef = useRef<number | undefined>(initialAccumulatedSeconds);

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  const calculateDisplaySeconds = useCallback(
    (accumulatedSeconds: number, timerStartedAt: string | null): number => {
      if (!timerStartedAt) {
        return accumulatedSeconds;
      }
      const startTime = new Date(timerStartedAt).getTime();
      const now = Date.now();
      const elapsedSinceStart = Math.floor((now - startTime) / 1000);
      return accumulatedSeconds + Math.max(0, elapsedSinceStart);
    },
    [],
  );

  const deriveState = useCallback(
    (
      accumulatedSeconds: number,
      timerStartedAt: string | null,
      phaseStatus: string,
    ): TimerState => {
      const displaySeconds = calculateDisplaySeconds(accumulatedSeconds, timerStartedAt);
      const isRunning = !!timerStartedAt;
      const hasStarted = phaseStatus !== 'not_started' || accumulatedSeconds > 0 || isRunning;
      const isPaused = hasStarted && !isRunning;
      return { isRunning, isPaused, hasStarted, displaySeconds };
    },
    [calculateDisplaySeconds],
  );

  const invalidateQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['task', taskId] });
    queryClient.invalidateQueries({ queryKey: ['sprint-tasks'] });
    queryClient.invalidateQueries({ queryKey: ['tasks'] });
  }, [queryClient, taskId]);

  // ---------------------------------------------------------------------------
  // Fetch timer status from REST API
  // ---------------------------------------------------------------------------

  const fetchTimerStatus = useCallback(async () => {
    if (!taskId) {
      setIsLoading(false);
      return;
    }

    try {
      const res = await taskService.timerStatus(taskId);
      const data = res.data.data;

      const accumulatedSeconds = data.accumulated_seconds ?? 0;
      const timerStartedAt = data.timer_started_at;
      timerStartedAtRef.current = timerStartedAt;

      const isRunning = data.running;
      const displaySeconds = calculateDisplaySeconds(accumulatedSeconds, timerStartedAt);
      const hasStarted = accumulatedSeconds > 0 || isRunning;
      const isPaused = hasStarted && !isRunning;

      setTimerState({ isRunning, isPaused, hasStarted, displaySeconds });
    } catch (error) {
      console.error('Error fetching timer status:', error);
    } finally {
      setIsLoading(false);
    }
  }, [taskId, calculateDisplaySeconds]);

  // ---------------------------------------------------------------------------
  // Initialisation
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    // If caller provided initial data, use it directly without fetching
    if (initialTimerStartedAt !== undefined) {
      const accSec = initialAccumulatedSeconds ?? 0;
      const timerStartedAt = initialTimerStartedAt ?? null;
      timerStartedAtRef.current = timerStartedAt;

      setTimerState(deriveState(accSec, timerStartedAt, initialPhaseStatus));
      setIsLoading(false);
    } else {
      fetchTimerStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // React to external changes in accumulated_seconds (e.g. from edit form)
  useEffect(() => {
    if (initialAccumulatedSeconds === prevAccumulatedSecondsRef.current) return;
    prevAccumulatedSecondsRef.current = initialAccumulatedSeconds;

    if (initialAccumulatedSeconds !== undefined && !timerState.isRunning) {
      setTimerState((prev) => ({
        ...prev,
        displaySeconds: initialAccumulatedSeconds,
        hasStarted: initialAccumulatedSeconds > 0 || prev.hasStarted,
      }));
    }
  }, [initialAccumulatedSeconds, timerState.isRunning]);

  // ---------------------------------------------------------------------------
  // Visibility change – recalculate on tab focus
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && timerStartedAtRef.current) {
        // Recalculate from the ref so we don't need to re-fetch
        setTimerState((prev) => {
          if (!prev.isRunning || !timerStartedAtRef.current) return prev;
          const accSec = initialAccumulatedSeconds ?? 0;
          return {
            ...prev,
            displaySeconds: calculateDisplaySeconds(accSec, timerStartedAtRef.current),
          };
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [calculateDisplaySeconds, initialAccumulatedSeconds]);

  // ---------------------------------------------------------------------------
  // Tick interval while running
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (timerState.isRunning) {
      intervalRef.current = setInterval(() => {
        setTimerState((prev) => ({
          ...prev,
          displaySeconds: prev.displaySeconds + 1,
        }));
      }, 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [timerState.isRunning]);

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  const startTimer = useCallback(async () => {
    setIsStarting(true);
    try {
      const res = await taskService.timerStart(taskId);
      const data = res.data.data;

      timerStartedAtRef.current = data.timer_started_at;

      setTimerState({
        isRunning: true,
        isPaused: false,
        hasStarted: true,
        displaySeconds: calculateDisplaySeconds(
          data.accumulated_seconds,
          data.timer_started_at,
        ),
      });

      invalidateQueries();
    } catch (error) {
      console.error('Error starting timer:', error);
      toast({
        title: 'Error',
        description: 'Failed to start timer',
        variant: 'destructive',
      });
    } finally {
      setIsStarting(false);
    }
  }, [taskId, toast, calculateDisplaySeconds, invalidateQueries]);

  const pauseTimer = useCallback(async () => {
    setIsPausing(true);
    try {
      const res = await taskService.timerPause(taskId);
      const data = res.data.data;

      timerStartedAtRef.current = null;

      setTimerState((prev) => ({
        ...prev,
        isRunning: false,
        isPaused: true,
        displaySeconds: data.accumulated_seconds,
      }));

      invalidateQueries();
    } catch (error) {
      console.error('Error pausing timer:', error);
      toast({
        title: 'Error',
        description: 'Failed to pause timer',
        variant: 'destructive',
      });
    } finally {
      setIsPausing(false);
    }
  }, [taskId, toast, invalidateQueries]);

  const resumeTimer = useCallback(async () => {
    setIsResuming(true);
    try {
      const res = await taskService.timerStart(taskId);
      const data = res.data.data;

      timerStartedAtRef.current = data.timer_started_at;

      setTimerState((prev) => ({
        ...prev,
        isRunning: true,
        isPaused: false,
        displaySeconds: calculateDisplaySeconds(
          data.accumulated_seconds,
          data.timer_started_at,
        ),
      }));

      invalidateQueries();
    } catch (error) {
      console.error('Error resuming timer:', error);
      toast({
        title: 'Error',
        description: 'Failed to resume timer',
        variant: 'destructive',
      });
    } finally {
      setIsResuming(false);
    }
  }, [taskId, toast, calculateDisplaySeconds, invalidateQueries]);

  const stopTimer = useCallback(
    async (deliverables?: string) => {
      setIsFinishing(true);
      try {
        const res = await taskService.timerStop(taskId);
        const data = res.data.data;
        const finalSeconds = data.accumulated_seconds;

        timerStartedAtRef.current = null;

        // If deliverables were provided, update the task with them
        if (deliverables !== undefined) {
          await taskService.update(taskId, { deliverables });
        }

        setTimerState({
          isRunning: false,
          isPaused: false,
          hasStarted: true,
          displaySeconds: finalSeconds,
        });

        toast({
          title: 'Task completed',
          description: `Total time: ${formatTime(finalSeconds)}`,
        });

        invalidateQueries();
      } catch (error) {
        console.error('Error stopping timer:', error);
        toast({
          title: 'Error',
          description: 'Failed to complete task',
          variant: 'destructive',
        });
      } finally {
        setIsFinishing(false);
      }
    },
    [taskId, toast, invalidateQueries],
  );

  return {
    displaySeconds: timerState.displaySeconds,
    isRunning: timerState.isRunning,
    isPaused: timerState.isPaused,
    hasStarted: timerState.hasStarted,
    isLoading,
    isStarting,
    isPausing,
    isResuming,
    isFinishing,
    startTimer,
    pauseTimer,
    resumeTimer,
    stopTimer,
  };
}
